import { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { getEventIntentFields } from '@pano-project/utils/eventIntent';
import compose from '../../utils/helper-functions/compose';
import { postMessage } from '../../utils/helper-functions/post-message';
import { changeInitTheme } from '../../state/actions';
import { getTransactionPaymentMethodKey } from '../../utils/helper-functions/get-transaction-payment-method-key';
import { getUrlHost } from '../../utils/helper-functions/get-url-host';
import store from '../../state/store';

const ConnectParentDomain = ({
  children,
  initTheme,
  changeInitTheme,
  currentGateway,
  resultOfPay,
}) => {
  // currentGateway starts as `false`, so read it with optional chaining.
  const payment_method_key = currentGateway?.payment_method_key;
  const option_id = currentGateway?.option_id;
  let { transaction } = resultOfPay;
  const { currentDomain } = initTheme;

  const profileInfo = useSelector(({ profileInfo }) => profileInfo, shallowEqual);
  const { cid, order_id } = useMemo(() => {
    const cid = (profileInfo?.customer?.pin || initTheme?.cid) ?? '';
    const order_id = (profileInfo?.order_id || initTheme?.order_id) ?? '';
    return { cid, order_id };
  }, [profileInfo, initTheme]);

  useEffect(() => {
    if (!initTheme.validate_domain) return;

    const receiveMessage = (event) => {
      // The listener lives for the whole page, so it reads the live
      // settings rather than the copy captured when it was added, and
      // writes only the domain: writing the captured copy back would
      // undo everything set since, such as the active tab.
      const liveInitTheme = store.getState().initTheme || {};
      const liveValidDomains = liveInitTheme.valid_domains || [];
      //if currentDomain is set no need to do it again.
      //if event origin is the same with current website one should not set the currentDomain to it.
      //If event origin does not exist in valid domains array then don't set it.
      if (
        liveInitTheme.currentDomain ||
        event.origin === window.origin ||
        !liveValidDomains.includes(getUrlHost(event.origin))
      )
        return;
      changeInitTheme({ currentDomain: event.origin });
    };

    window.addEventListener('message', receiveMessage);
    postMessage({ init: true });

    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  }, []);

  //transaction event
  useEffect(() => {
    if (!transaction || !currentDomain) return;
    postMessage(
      {
        event_type: 'transaction',
        ...transaction,
        payment_method: getTransactionPaymentMethodKey(transaction),
        cid,
        order_id,
        ...getEventIntentFields({ initTheme }),
      },
      currentDomain,
    );
  }, [transaction, currentDomain]);

  //payment method selected event: fires when the selected option changes and
  //sends the stable payment method key (two options may share a key)
  useEffect(() => {
    if (typeof payment_method_key !== 'string' || payment_method_key.length === 0 || !currentDomain)
      return;

    postMessage(
      {
        event_type: 'payment_method_selected',
        payment_method: payment_method_key,
        cid,
        order_id,
        ...getEventIntentFields({ initTheme }),
      },
      currentDomain,
    );
  }, [option_id, payment_method_key, currentDomain]);

  return children;
};

const mapStateToProps = ({ currentGateway, initTheme, resultOfPay }) => {
  return { currentGateway, initTheme, resultOfPay };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      changeInitTheme,
    },
    dispatch,
  );
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(ConnectParentDomain);
